import React, { useEffect, useRef, useState } from "react";
import { auth } from "./firebase-config";
import {
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { useLocation } from "react-router-dom";
import { isPortalPath } from "./utils/isPortalPath";

const INACTIVITY_TIMEOUT = 60 * 60 * 1000;
const CHECK_INTERVAL = 30 * 1000;
const LAST_ACTIVITY_KEY = "kinova:lastActivity";

export const SessionProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();

  const lastActivityRef = useRef(Date.now());
  const isSigningOutRef = useRef(false);

  const updateLastActivity = (valueMs) => {
    const now = Number.isFinite(Number(valueMs)) ? Number(valueMs) : Date.now();
    lastActivityRef.current = now;
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    } catch {
      // ignore storage issues
    }
  };

  // 1) Persistence mode based on route
  useEffect(() => {
    async function initPersistence() {
      try {
        if (isPortalPath(location.pathname)) {
          await setPersistence(auth, inMemoryPersistence);
          return;
        }

        // Staff/back-office (including /checkin console/status): prefer durable
        try {
          await setPersistence(auth, browserLocalPersistence);
        } catch {
          try {
            await setPersistence(auth, browserSessionPersistence);
          } catch {
            await setPersistence(auth, inMemoryPersistence);
          }
        }
      } catch (err) {
        console.warn("[SessionProvider] setPersistence failed:", err);
      }
    }

    initPersistence();
  }, [location.pathname]);

  // 2) Auth listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsLoggedIn(true);
        updateLastActivity();
      } else {
        setIsLoggedIn(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // 3) Cross-tab sync: if another tab updates LAST_ACTIVITY_KEY, keep this tab in sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== LAST_ACTIVITY_KEY) return;
      const v = Number(e.newValue);
      if (!Number.isFinite(v)) return;

      if (v > lastActivityRef.current) {
        lastActivityRef.current = v;
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // 4) Inactivity enforcement (timestamp-based)
  useEffect(() => {
    if (!isLoggedIn) return;

    try {
      const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
      if (stored) {
        lastActivityRef.current = Number(stored) || Date.now();
      } else {
        updateLastActivity();
      }
    } catch {
      // ignore
    }

    const handleSignOut = async () => {
      if (isSigningOutRef.current) return;
      isSigningOutRef.current = true;

      try {
        await signOut(auth);
      } catch (e) {
        console.error("[SessionProvider] signOut failed:", e);
      }

      // Do not nuke all storage; only remove our session activity key.
      try {
        localStorage.removeItem(LAST_ACTIVITY_KEY);
      } catch {
        // ignore
      }

      try {
        sessionStorage.clear();
      } catch {
        // ignore
      }

      window.location.href = "/login";
    };

    const checkInactivity = () => {
      const now = Date.now();
      const lastActivity = lastActivityRef.current;
      const diff = now - lastActivity;

      if (diff > INACTIVITY_TIMEOUT) {
        console.log(
          `[SessionProvider] Inactivity timeout exceeded. diff=${diff}ms lastActivity=${new Date(
            lastActivity
          ).toISOString()}`
        );
        handleSignOut();
      }
    };

    const activityHandler = () => updateLastActivity();

    // Keep these events broad enough for mobile
    window.addEventListener("mousemove", activityHandler);
    window.addEventListener("keydown", activityHandler);
    window.addEventListener("click", activityHandler);
    window.addEventListener("touchstart", activityHandler);
    window.addEventListener("scroll", activityHandler, { passive: true });

    const visibilityHandler = () => {
      if (document.visibilityState === "visible") checkInactivity();
    };
    const focusHandler = () => checkInactivity();

    document.addEventListener("visibilitychange", visibilityHandler);
    window.addEventListener("focus", focusHandler);

    const intervalId = window.setInterval(checkInactivity, CHECK_INTERVAL);
    checkInactivity();

    return () => {
      window.removeEventListener("mousemove", activityHandler);
      window.removeEventListener("keydown", activityHandler);
      window.removeEventListener("click", activityHandler);
      window.removeEventListener("touchstart", activityHandler);
      window.removeEventListener("scroll", activityHandler);
      document.removeEventListener("visibilitychange", visibilityHandler);
      window.removeEventListener("focus", focusHandler);
      window.clearInterval(intervalId);
    };
  }, [isLoggedIn]);

  return <>{children}</>;
};